/* eslint-disable react-hooks/rules-of-hooks */
import dropdownArrow from "@assets/actions/dropdownArrow.svg";
import JobsCard from "@components/Blocks/Card/JobsCard";
import { JOBS_CATEGORY_DATA } from "@config/JobsConstant";
import MainBlock from "@container/MainBlock";
import MainContainer from "@container/MainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { Imu1CompanionAdsInfiniteScroll } from "@elements/Advertisement/variant/AdsCompanion";
import Heading from "@elements/Typography/Heading";
import fetchTMApi from "@helper/fetchTMApi";
import { displayAfter, getCurrentPath } from "@helper/utils";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import {
  CreateLoadMoreJobsHandler,
  CreateLoadMoreJobsHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreJobsHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import ErrorPage from "@pages/ErrorPage";
import NotFound from "@pages/NotFound";
import {
  CustomContext,
  ResponseType,
  TRouteWithoutRedirect,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { MoreJobsRequestData } from "@typings/MoreJobsApi";
import { JobsPayloadResponseType } from "@typings/PagePayloadType";
import { JobsDetailsItem } from "@typings/Storage.d";
import {
  ChangeEvent,
  Fragment,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";

interface CheckboxItem {
  url: string;
  title: string;
}

const PAGE_SIZE = 5;
const INITIAL_JOBS_COUNT = 10;

export default function Jobs(): ReactElement {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<JobsPayloadResponseType, string>
  > = useRouteContext();
  // Load jobs data.
  const dataLoaderResponse = routeContext.context;
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const jobsPayloadData = dataLoaderResponse.payload.data;
  const sourceJobsData: Record<string, number> =
    dataLoaderResponse.payload.sourceData;

  const jobsCount = (sourceJobsCount?: number | null): string =>
    sourceJobsCount == null || isNaN(sourceJobsCount)
      ? ""
      : sourceJobsCount < 1000
        ? `( ${sourceJobsCount} )`
        : sourceJobsCount < 1_000_000
          ? `( ${Math.floor(sourceJobsCount / 1000)}K+ )`
          : `( ${Math.floor(sourceJobsCount / 1_000_000)}M+ )`;

  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const currentPath = getCurrentPath();

  const sectionName = "jobs";
  const pageName = "வேலை வாய்ப்பு";
  let pageTitle = "";
  if (currentPath.split("/").length > 2) {
    pageTitle = "Job Listing";
  }

  const navigate = useNavigate();
  const [checkedUrl, setCheckedUrl] = useState<string | null>(null);

  const checkboxItems: CheckboxItem[] = [
    {
      url: "MyCareersFuture",
      title: `MyCareersFuture ${
        Object.keys(sourceJobsData).length > 0
          ? jobsCount(sourceJobsData["MyCareersFuture"])
          : ""
      }`,
    },
  ];

  const jobsCategoryOptions = [
    { key: "all", value: "அனைத்து வேலைகளும்" },
    ...JOBS_CATEGORY_DATA.map((category) => ({
      key: category.category_url,
      value: category.job_category_tamil,
    })),
  ];
  const [selectedCategory, setSelectedCategory] = useState<string>(
    jobsCategoryOptions[0].key,
  );

  const [isPreferedCategoryOpen, setIsPreferedCategoryOpen] =
    useState<boolean>(false);

  const togglePreferedCategoryDropdown = () => {
    setIsPreferedCategoryOpen((prev) => !prev);
  };

  useEffect(() => {
    const splitedCurrentPath = currentPath.split("/");
    // Set default value for checkbox.
    const checkboxMatch = checkboxItems.find(
      (item) =>
        item.url === splitedCurrentPath[2] ||
        item.url === splitedCurrentPath[3],
    );
    if (checkboxMatch) {
      setCheckedUrl(checkboxMatch.url);
    }

    // Set default value for select list.
    const selectListMatch = jobsCategoryOptions.find(
      (item) =>
        item.key === splitedCurrentPath[2] ||
        item.key === splitedCurrentPath[3],
    );
    if (selectListMatch) {
      setSelectedCategory(selectListMatch.key);
    }

    const jobsIdsToAdd: number[] = [];
    jobsPayloadData.forEach((job) => {
      jobsIdsToAdd.push(job.job_id);
    });
    setRenderedJobIds((prevState) => new Set([...prevState, ...jobsIdsToAdd]));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCategoryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(event.target.value);
    void navigate(`/jobs/${event.target.value}`);

    if (checkedUrl !== "null") {
      setCheckedUrl(null);
    }
  };

  const handleCheckboxChange = (url: string) => {
    setCheckedUrl((prev) => {
      const isNew = prev !== url;
      if (isNew) {
        const splitUrl = currentPath.split("/").filter(Boolean);
        const lastUrlParam = splitUrl[splitUrl.length - 1];
        const urlsInCheckboxes = checkboxItems.map((item) =>
          item.url.split("/").filter(Boolean).pop(),
        );
        const currentUrlIsInCheckboxes =
          urlsInCheckboxes.includes(lastUrlParam);
        if (currentUrlIsInCheckboxes) {
          splitUrl.pop();
          splitUrl.push(url);
          const newUrl = "/" + splitUrl.join("/");
          void navigate(newUrl);
        } else {
          void navigate(
            `${currentPath}${currentPath === "/jobs" ? "/all" : ""}/${url}`,
          );
        }

        TmCustomGaEvent({
          event: "customEvent",
          eventCategory: "Job Search Filter",
          eventAction: "Click",
          eventLabel: url,
        });
      } else {
        void navigate(`/jobs`);
      }
      return isNew ? url : null;
    });
  };

  // Infinite scroll.
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<JobsDetailsItem[]>([]);
  const [renderedJobIds, setRenderedJobIds] = useState(new Set<number>());
  const [hasMoreJobs, setHasMoreJobs] = useState<boolean>(true);

  const showLoadMore: boolean = jobsPayloadData.length == INITIAL_JOBS_COUNT;

  const handleLoadMore = async () => {
    const lastLoadedJob =
      loadMoreData.length == 0 ? jobsPayloadData.at(-1) : loadMoreData.at(-1);
    const requestData: MoreJobsRequestData = {
      category: dataLoaderResponse.payload.category,
      source: dataLoaderResponse.payload.source,
      size: "5",
      excludeIds: Array.from(renderedJobIds).join(","),
    };

    const fetchMoreJobsData = async (
      requestData: MoreJobsRequestData,
    ): Promise<JobsDetailsItem[] | []> => {
      const queryParams = new URLSearchParams({
        category: requestData.category as string,
        source: requestData.source as string,
        size: requestData.size as string,
        excludeIds: requestData.excludeIds as string,
      }).toString();
      const response: JobsDetailsItem[] = await fetchTMApi(
        `processed-json-jobs?${queryParams}`,
        "GET",
      );

      return response;
    };

    const createLoadMoreProps: CreateLoadMoreJobsHandlerProps = {
      requestData,
      fetchFunction: fetchMoreJobsData,
      setHasMoreJobs,
      setRenderedJobIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedJobIds,
      lastLoadedJob,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreJobsHandler(createLoadMoreProps);
    return response;
  };

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  return (
    <MainContainer
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock
          sectionName={sectionName}
          showTitle={false}
          pageName={pageTitle}
        >
          <Heading className="leading-650 mb-2 font-semibold text-md font-sans text-black-500">
            {pageName}
          </Heading>
          <div className="pb-3">
            <div className="relative inline-block ml-sm mb-4">
              <select
                id="jobs-category"
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="text-base font-sans leading-5 rounded-l-sm px-3 py-1.5 appearance-none h-9 text-black-100 bg-white-100 border border-gray-400 focus-visible:!border-gray-400 focus:outline-none"
              >
                {jobsCategoryOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.value}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex rounded-r-sm items-center pointer-events-none bg-red-950 text-white-100 px-100 py-xxs">
                <img
                  src={dropdownArrow}
                  alt={"dropdown-arrow"}
                  width={12}
                  height={12}
                />
              </div>
            </div>
            {jobsPayloadData.length > 0 && (
              <>
                <div className="block my-4 border-b border-b-pink-300 border-b-solid pb-4">
                  <button
                    onClick={togglePreferedCategoryDropdown}
                    className={twMerge(
                      "dropdown-button flex text-red-950 font-primary font-semibold text-base leading-650 items-center after:inline-block after:content-[''] after:ml-1 after:border-solid after:border-r-4 after:border-r-transparent after:border-l-4 after:border-l-transparent",
                      isPreferedCategoryOpen
                        ? "after:border-t-0 after:border-b-4 after:border-b-red-950"
                        : "after:border-b-0 after:border-t-4 after:border-t-red-950",
                    )}
                  >
                    விருப்பமான வேலையைத் தேர்ந்தெடுக்க
                  </button>
                  <div
                    className={twMerge(
                      "transition-all duration-700 ease-in-out overflow-hidden",
                      isPreferedCategoryOpen
                        ? "opacity-100 max-h-96 translate-y-0"
                        : "opacity-0 max-h-0 -translate-y-2",
                    )}
                  >
                    <div className="mb-4 font-sans font-bold text-xs leading-7">
                      கீழே உள்ளவற்றுள் தேர்ந்தெடுக்கவும்
                    </div>
                    <div className="pl-11 inline-grid">
                      {checkboxItems.map((item, index) => (
                        <label
                          key={index}
                          className="h-17 pt-3 text-1sm font-sans font-normal text-gray-100"
                        >
                          <input
                            id={`checkbox-${index}`}
                            type="checkbox"
                            checked={checkedUrl === item.url}
                            onChange={() => {
                              handleCheckboxChange(item.url);
                            }}
                            className="mr-1"
                          />
                          {item.title}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="block">
            {jobsPayloadData.length > 0 ? (
              <>
                {[...jobsPayloadData, ...loadMoreData].map((job, index) => (
                  <Fragment key={job.job_id}>
                    <JobsCard
                      key={index}
                      job_id={job.job_id}
                      job_category_url={job.job_category_url}
                      job_title={job.job_title}
                      job_description={job.job_description}
                      job_company={job.job_company}
                      job_company_logo={job.job_company_logo}
                      job_url={job.job_url}
                      job_salary={job.job_salary}
                      job_source={job.job_source}
                      updated_date={job.updated_date}
                      sort_order={job.sort_order}
                      is_published={job.is_published}
                    />
                    {displayAfter(index + 1, PAGE_SIZE) && (
                      <Imu1CompanionAdsInfiniteScroll
                        sectionName={sectionName}
                        index={(index + 1) / PAGE_SIZE}
                        pageAdTargetType={pageAdTargetValue}
                      />
                    )}
                  </Fragment>
                ))}
                {showLoadMore ? (
                  <div className="flex w-full justify-center mb-xl">
                    <LoadMore
                      rootClassName="my-4 w-full"
                      onLoadMore={handleLoadMore}
                      hasMore={hasMoreJobs}
                      loadText="Load More"
                      mobileInfiniteScroll={true}
                      pageNumber={pageNumber}
                      sectionName={sectionName}
                    />
                  </div>
                ) : null}
              </>
            ) : (
              <div className="font-normal text-xs text-gray-100">
                No result found
              </div>
            )}
          </div>
        </MainBlock>
        <SideBlock
          sectionName={sectionName}
          className="w-full lg:block"
          pageAdTargetValue={pageAdTargetValue}
          hideAdForMobile={true}
        />
      </WrapperBlock>
    </MainContainer>
  );
}
